package com.siriusxm.transcripts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * VTT Viewer component that displays WebVTT transcript content in a formatted list view.
 *
 * The viewer displays each cue as a row with:
 * - Timestamp on the left (in monospace font)
 * - Transcript text on the right
 * - Timeline scrubber for navigation
 *
 * @param vttContent The raw WebVTT text content to parse and display
 * @param showViewToggle Whether to show the Formatted/Raw toggle (default: false)
 */
@Composable
fun VttViewer(vttContent: String, showViewToggle: Boolean = false) {
    val document = remember(vttContent) { parseVTT(vttContent) }
    var view by remember { mutableStateOf("formatted") }
    var currentTime by remember { mutableStateOf(0L) }
    var lastProgrammaticScrollTime by remember { mutableStateOf(0.0) }

    // Calculate total duration from the last cue
    val totalDuration = remember(document) {
        document.cues.lastOrNull()?.endTime ?: 0L
    }

    Div {
        // View toggle (if enabled)
        if (showViewToggle) {
            Div({
                style {
                    textAlign("right")
                    margin(0.3.cssRem, 0.px)
                    marginBottom(8.px)
                }
            }) {
                com.siriusxm.pia.components.buttonRadioGroup(view) {
                    option("Formatted", "formatted")
                    option("Raw", "raw")
                    onChange {
                        view = it
                    }
                }
            }
        }

        // Timeline scrubber (only in formatted view with cues)
        if (view == "formatted" && document.cues.isNotEmpty()) {
            Div({
                style {
                    marginBottom(12.px)
                    padding(12.px, 16.px)
                    backgroundColor(SXMUI.containerContentBackgroundColor.value())
                    border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
                    borderRadius(SXMUI.boxRadius.value())
                }
            }) {
                // Timeline label
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("justify-content", "space-between")
                        marginBottom(8.px)
                        fontSize(SXMUI.smallFontSize.value())
                        color(SXMUI.subtleTextColor.value())
                    }
                }) {
                    Span { Text(formatTimestamp(currentTime)) }
                    Span { Text(formatTimestamp(totalDuration)) }
                }

                // Timeline scrubber
                TimelineScrubber(
                    currentTime = currentTime,
                    totalDuration = totalDuration,
                    onTimeChange = { newTime ->
                        lastProgrammaticScrollTime = kotlinx.browser.window.performance.now()
                        currentTime = newTime
                    }
                )
            }
        }

        // Content view
        val scrollContainerId = remember { "vtt-scroll-container-${vttContent.hashCode()}" }
        Div({
            id(scrollContainerId)
            style {
                backgroundColor(SXMUI.containerContentBackgroundColor.value())
                border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
                borderRadius(SXMUI.boxRadius.value())
                overflow("auto")
                maxHeight(600.px)
            }
            // Update scrubber position when scrolling (but not when scrolling from slider)
            addEventListener("scroll") { event ->
                // Only update if enough time has passed since last programmatic scroll (100ms threshold)
                val timeSinceLastProgrammaticScroll = kotlinx.browser.window.performance.now() - lastProgrammaticScrollTime
                if (view == "formatted" && document.cues.isNotEmpty() && timeSinceLastProgrammaticScroll > 100) {
                    val container = event.target as? org.w3c.dom.HTMLElement
                    if (container != null) {
                        val scrollTop = container.scrollTop

                        // If scrolled to the very top, set time to 0
                        if (scrollTop <= 1.0) {
                            currentTime = 0L
                        } else {
                            // Find the first visible cue by checking scroll position
                            var visibleCue: VTTCue? = null

                            for (cue in document.cues) {
                                val cueId = "vtt-cue-${scrollContainerId}-${cue.index}"
                                val cueElement = kotlinx.browser.document.getElementById(cueId) as? org.w3c.dom.HTMLElement
                                if (cueElement != null) {
                                    val cueTop = cueElement.offsetTop.toDouble()
                                    // If this cue is at or past the scroll position, it's visible
                                    if (cueTop >= scrollTop) {
                                        visibleCue = cue
                                        break
                                    }
                                }
                            }

                            // Update current time to match the visible cue
                            if (visibleCue != null) {
                                currentTime = visibleCue.startTime
                            }
                        }
                    }
                }
            }
        }) {
            if (view == "formatted") {
                // Formatted view
                if (document.cues.isEmpty()) {
                    Div({
                        style {
                            padding(16.px)
                            color(SXMUI.subtleTextColor.value())
                            textAlign("center")
                        }
                    }) {
                        Text("No transcript cues found")
                    }
                } else {
                    document.cues.forEach { cue ->
                        CueRow(cue, scrollContainerId)
                    }

                    // Scroll to current time (when slider is moved)
                    androidx.compose.runtime.LaunchedEffect(currentTime) {
                        // Check if this was triggered by scrubber (within 50ms of last change)
                        val timeSinceLastChange = kotlinx.browser.window.performance.now() - lastProgrammaticScrollTime
                        if (timeSinceLastChange < 50) {
                            val container = kotlinx.browser.document.getElementById(scrollContainerId)
                            if (container != null) {
                                if (currentTime <= 0L) {
                                    // Scroll to top when at zero
                                    container.scrollTop = 0.0
                                } else {
                                    // Find the cue at the current time
                                    val targetCue = document.cues.findLast { it.startTime <= currentTime }
                                    if (targetCue != null) {
                                        val cueId = "vtt-cue-${scrollContainerId}-${targetCue.index}"
                                        val cueElement = kotlinx.browser.document.getElementById(cueId)
                                        if (cueElement != null) {
                                            // Scroll to the cue, positioning it near the top
                                            val cueTop = (cueElement as? org.w3c.dom.HTMLElement)?.offsetTop?.toDouble() ?: 0.0
                                            container.scrollTop = cueTop - 20 // 20px offset from top
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            } else {
                // Raw VTT view
                org.jetbrains.compose.web.dom.Pre({
                    style {
                        padding(16.px)
                        margin(0.px)
                        color(SXMUI.textColor.value())
                        fontFamily("ui-monospace", "Menlo", "Monaco", "Cascadia Code", "monospace")
                        fontSize(13.px)
                        property("line-height", "1.5")
                        property("white-space", "pre-wrap")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(vttContent)
                }
            }
        }
    }
}

/**
 * Displays a single VTT cue as a row with timestamp, optional speaker, and text.
 *
 * @param cue The VTT cue to display
 * @param containerId The ID of the scroll container (for generating unique cue IDs)
 */
@Composable
private fun CueRow(cue: VTTCue, containerId: String = "") {
    Div({
        id("vtt-cue-${containerId}-${cue.index}")
        style {
            display(DisplayStyle.Flex)
            padding(12.px, 16.px)
            property("border-bottom", "1px solid ${SXMUI.borderDividerDefault.value()}")
            gap(16.px)
            property("align-items", "flex-start")
        }
    }) {
        // Timestamp column
        Span({
            style {
                color(SXMUI.subtleTextColor.value())
                width(80.px)
                flexShrink(0)
                fontFamily("ui-monospace", "Menlo", "Monaco", "Cascadia Code", "monospace")
                fontSize(SXMUI.smallFontSize.value())
                property("user-select", "none")
            }
        }) {
            Text(formatTimestamp(cue.startTime))
        }

        // Text column (with optional speaker)
        Div({
            style {
                flex(1)
                property("line-height", "1.5")
            }
        }) {
            // Display speaker name if present
            if (cue.speaker != null) {
                Span({
                    style {
                        color(SXMUI.brandColor.value())
                        fontWeight(600)
                        display(DisplayStyle.Block)
                        marginBottom(4.px)
                    }
                }) {
                    Text(cue.speaker)
                }
            }

            // Display transcript text
            Span({
                style {
                    color(SXMUI.textColor.value())
                    property("white-space", "pre-wrap")
                    property("word-break", "break-word")
                }
            }) {
                Text(cue.text)
            }
        }
    }
}

// Sample VTT data for testing
val SAMPLE_VTT = """
WEBVTT

00:00:00.000 --> 00:00:03.500
Welcome to this episode of the podcast.

00:00:03.500 --> 00:00:07.000
Today we're talking about technology and innovation.

00:00:07.000 --> 00:00:12.500
Our guest is an expert in artificial intelligence
and machine learning.

00:00:12.500 --> 00:00:16.000
Let's dive right into the conversation.

00:00:16.000 --> 00:00:22.000
First, can you tell us about your background
and how you got started in this field?

00:00:22.000 --> 00:00:28.500
It's a fascinating journey that began back in college
when I first discovered computer science.

00:00:28.500 --> 00:00:35.000
I was immediately drawn to the idea of teaching machines
to learn and make decisions on their own.

00:00:35.000 --> 00:00:40.000
That led me down a path of research and development
that continues to this day.

00:00:40.000 --> 00:00:45.500
The field has evolved so much since then,
with breakthroughs happening almost daily.

00:00:45.500 --> 00:00:50.000
Today, AI and machine learning are transforming
every industry you can think of.

00:00:50.000 --> 00:00:55.000
From healthcare to transportation,
from entertainment to finance.

00:00:55.000 --> 00:01:00.000
It's an incredibly exciting time to be working
in this space.
""".trimIndent()
